import { Trans, useTranslation } from "react-i18next";
import { CalendarClock, Printer, UserRoundSearch, WineOff, type LucideIcon } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { GetAllExpeditionBoResponseDtoExpedition } from "@/types/api/api-types";
import { ActionDataBo } from "@/types/tableAction";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { NoteSubType } from "@/constants/enums/noteSubType.enum";
import { NoteType } from "@/constants/enums/noteType.enum";
import i18n from "@/i18n";

export type LateShippingQualificationOption = {
  subType: NoteSubType;
  title: string;
  helper: string;
  icon: LucideIcon;
  dateLabel: string | null;
  defaultDaysFromNow: number | null;
  badgeClassName: string;
  iconClassName: string;
  badgeLabel: (formattedDate: string) => string;
  tooltipLabel: (formattedDate: string) => string;
};

// Les libellés sont résolus à la lecture (getters / fonctions), jamais au chargement du module :
// la constante reste exportée telle quelle pour les appelants, et suit la langue courante.
export const LATE_SHIPPING_QUALIFICATION_OPTIONS: LateShippingQualificationOption[] = [
  {
    subType: NoteSubType.POSTPONED,
    get title() {
      return i18n.t("boExpeditions:lateShippingQualification.postponed.title");
    },
    get helper() {
      return i18n.t("boExpeditions:lateShippingQualification.postponed.helper");
    },
    icon: CalendarClock,
    get dateLabel() {
      return i18n.t("boExpeditions:lateShippingQualification.postponed.dateLabel");
    },
    defaultDaysFromNow: null,
    badgeClassName: "border-purple-300 bg-purple-100 text-purple-700",
    iconClassName: "text-purple-600",
    badgeLabel: (date) =>
      i18n.t("boExpeditions:lateShippingQualification.postponed.badge", { date }),
    tooltipLabel: (date) =>
      i18n.t("boExpeditions:lateShippingQualification.postponed.tooltip", { date }),
  },
  {
    subType: NoteSubType.WINES_UNAVAILABLE,
    get title() {
      return i18n.t("boExpeditions:lateShippingQualification.winesUnavailable.title");
    },
    get helper() {
      return i18n.t("boExpeditions:lateShippingQualification.winesUnavailable.helper");
    },
    icon: WineOff,
    get dateLabel() {
      return i18n.t("boExpeditions:lateShippingQualification.winesUnavailable.dateLabel");
    },
    defaultDaysFromNow: 7,
    badgeClassName: "border-amber-300 bg-amber-100 text-amber-800",
    iconClassName: "text-amber-700",
    badgeLabel: (date) =>
      i18n.t("boExpeditions:lateShippingQualification.winesUnavailable.badge", { date }),
    tooltipLabel: (date) =>
      i18n.t("boExpeditions:lateShippingQualification.winesUnavailable.tooltip", { date }),
  },
  {
    subType: NoteSubType.TO_PRINT,
    get title() {
      return i18n.t("boExpeditions:lateShippingQualification.toPrint.title");
    },
    get helper() {
      return i18n.t("boExpeditions:lateShippingQualification.toPrint.helper");
    },
    icon: Printer,
    dateLabel: null,
    defaultDaysFromNow: null,
    badgeClassName: "border-blue-300 bg-blue-100 text-blue-700",
    iconClassName: "text-blue-600",
    badgeLabel: () => i18n.t("boExpeditions:lateShippingQualification.toPrint.badge"),
    tooltipLabel: () => i18n.t("boExpeditions:lateShippingQualification.toPrint.tooltip"),
  },
  {
    subType: NoteSubType.WAITING_CUSTOMER,
    get title() {
      return i18n.t("boExpeditions:lateShippingQualification.waitingCustomer.title");
    },
    get helper() {
      return i18n.t("boExpeditions:lateShippingQualification.waitingCustomer.helper");
    },
    icon: UserRoundSearch,
    get dateLabel() {
      return i18n.t("boExpeditions:lateShippingQualification.waitingCustomer.dateLabel");
    },
    defaultDaysFromNow: 3,
    badgeClassName: "border-slate-300 bg-slate-100 text-slate-700",
    iconClassName: "text-slate-600",
    badgeLabel: (date) =>
      i18n.t("boExpeditions:lateShippingQualification.waitingCustomer.badge", { date }),
    tooltipLabel: (date) =>
      i18n.t("boExpeditions:lateShippingQualification.waitingCustomer.tooltip", { date }),
  },
];

export const getLateShippingQualificationOption = (
  subType: string | null | undefined,
): LateShippingQualificationOption | undefined =>
  LATE_SHIPPING_QUALIFICATION_OPTIONS.find((option) => option.subType === subType);

export const getLateShippingQualificationNote = <T extends { type: string }>(
  notes: T[] | undefined,
): T | undefined => notes?.find((note) => note.type === NoteType.LATE_SHIPPING_QUALIFICATION);

export const NOTE_DATE_FORMAT = "yyyy-MM-dd";

export const parseQualificationDate = (note: string | undefined): Date | undefined => {
  if (!note) return undefined;
  const parsed = parseISO(note);
  return isValid(parsed) ? parsed : undefined;
};

export const formatQualificationDate = (note: string | undefined): string => {
  const parsed = parseQualificationDate(note);
  return parsed ? format(parsed, "dd/MM/yy") : "";
};

type Props = {
  row: GetAllExpeditionBoResponseDtoExpedition;
  setActionData: (actionData: ActionDataBo) => void;
};

export const LateShippingQualificationBadge = ({ row, setActionData }: Props) => {
  const { t } = useTranslation("boExpeditions");
  const qualification = getLateShippingQualificationNote(row.expeditionNotes);
  const option = getLateShippingQualificationOption(qualification?.subType);

  if (!qualification || !option) return null;

  const Icon = option.icon;
  const formattedDate = formatQualificationDate(qualification.note);
  const label = option.badgeLabel(formattedDate);
  const initials =
    qualification.createdBy.firstName.slice(0, 1).toUpperCase() +
    qualification.createdBy.lastName.slice(0, 1).toUpperCase();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "mt-0.5 flex w-full cursor-pointer items-center gap-1 rounded-md border p-1",
            option.badgeClassName,
          )}
          onClick={(e) => {
            e.stopPropagation();
            setActionData({ expedition: row, action: "LATE_SHIPPING_QUALIFICATION" });
          }}
        >
          <Icon className={cn("size-3.5 shrink-0", option.iconClassName)} />
          <p className="truncate text-[.85rem] leading-tight font-semibold">{label}</p>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-[280px] p-2" side="bottom">
        <Trans
          t={t}
          i18nKey="lateShippingQualification.badgeTooltip"
          values={{ label: option.tooltipLabel(formattedDate), initials }}
          components={{ bold: <b /> }}
        />
      </TooltipContent>
    </Tooltip>
  );
};
